/**
 * Motion editor types.
 *
 * v1 is a scene lane: a VideoScene does NOT own artboard content. It holds
 * timing/transition metadata and references a ProjectPage by `pageId`. The
 * Fabric artboard stays in `Project.pages[]`.
 *
 * v2 is the Movie Studio timeline: independent clips on text, visual, caption,
 * and audio tracks. A scene clip still points at a page. Media clips store
 * refs only — bytes stay in Storage or IndexedDB.
 *
 * Per-layer animation is the exception: it lives ON the Fabric object as
 * `craftaraAnim` (allowlisted in LAYER_CUSTOM_PROPS), not in VideoScene.
 * Rationale: (a) the offscreen frame compositor gets it for free from
 * loadFromJSON with no side lookup, (b) copy/paste/duplicate/group carry it,
 * (c) animation edits become undoable through the existing canvas history,
 * which is what users expect. Cost is ~120 bytes per animated layer.
 */

/* ---------- easing + presets ---------- */

/** Named easing curves. Names (not functions) so they serialize to Firestore. */
export type EasingId =
  | 'linear'
  | 'ease-in'
  | 'ease-out'
  | 'ease-in-out'
  /** t*t*(3-2t) — matches the ease already used in createKenBurnsReel. */
  | 'smoothstep'
  | 'back-out'
  | 'spring-soft';

export type AnimationDirection = 'left' | 'right' | 'up' | 'down' | 'center';
export type AnimationChannel = 'in' | 'out' | 'emphasis';

export type AnimationPresetId =
  | 'none'
  | 'fade-in' | 'slide-in' | 'rise' | 'pop-in' | 'pan-in' | 'wipe-in'
  | 'fade-out' | 'slide-out' | 'sink' | 'pop-out' | 'pan-out' | 'wipe-out'
  | 'ken-burns' | 'breathe' | 'float' | 'pulse' | 'wiggle' | 'spin-slow'
  /** Declared for forward compat; status 'planned' until Phase 6+. */
  | 'blur-in' | 'blur-out' | 'typewriter';

/** Object props an animation is allowed to touch. Drives the restore scope. */
export type AnimatedProp =
  | 'left' | 'top' | 'scaleX' | 'scaleY' | 'angle'
  | 'opacity' | 'skewX' | 'skewY' | 'visible' | 'clipPath' | 'text';

/** Static catalog entry for one animation preset. */
export interface AnimationPresetDef {
  id: AnimationPresetId;
  label: string;
  channel: AnimationChannel;
  defaultDurationMs: number;
  defaultEasing: EasingId;
  supportsDirection: boolean;
  /** Props this preset writes — the snapshot/restore scope. */
  touches: readonly AnimatedProp[];
  hint: string;
  /** 'planned' presets render as no-ops and are hidden from the picker. */
  status: 'shipped' | 'planned';
}

/* ---------- per-layer animation ---------- */

/** One timed animation step on a layer. */
export interface LayerAnimationStep {
  preset: AnimationPresetId;
  durationMs: number;
  /** `in`: ms after scene start. `out`: ms before scene end. Ignored for emphasis. */
  delayMs: number;
  easing: EasingId;
  direction?: AnimationDirection;
  /** 0..2 preset-specific magnitude multiplier. 1 = preset default. */
  intensity?: number;
}

/** Emphasis runs across the scene rather than at its edges. */
export interface LayerEmphasisStep extends LayerAnimationStep {
  /** Repeat for the whole scene duration (default true). */
  loop?: boolean;
}

/** Stored on the Fabric object as `craftaraAnim`. */
export interface LayerAnimation {
  /** Schema version for forward migration. Always 1 today. */
  v: 1;
  in?: LayerAnimationStep;
  out?: LayerAnimationStep;
  emphasis?: LayerEmphasisStep;
}

/* ---------- per-frame resolution ---------- */

/** Absolute object transform at a point in time. */
export interface LayerFrameState {
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  angle: number;
  opacity: number;
  skewX: number;
  skewY: number;
  visible: boolean;
}

/**
 * Presets emit DELTAS, never absolute states. This is the central design
 * choice: in + out + emphasis compose by add/multiply over the authored
 * baseline, so they never clobber each other and the baseline is always
 * recoverable by discarding the deltas.
 */
export interface LayerDelta {
  dLeft?: number;
  dTop?: number;
  mulScaleX?: number;
  mulScaleY?: number;
  dAngle?: number;
  mulOpacity?: number;
  dSkewX?: number;
  dSkewY?: number;
  visible?: boolean;
}

/* ---------- transitions ---------- */

export type TransitionId =
  | 'none' | 'cut' | 'crossfade' | 'dip-to-color'
  | 'slide' | 'push' | 'wipe' | 'zoom-blur';

/** Transition INTO a scene. Its duration overlaps the previous scene's tail. */
export interface SceneTransition {
  id: TransitionId;
  durationMs: number;
  direction?: AnimationDirection;
  /** dip-to-color only. */
  color?: string;
  easing?: EasingId;
}

/** Static catalog entry for one transition. */
export interface TransitionDef {
  id: TransitionId;
  label: string;
  defaultDurationMs: number;
  supportsDirection: boolean;
  supportsColor: boolean;
  hint: string;
}

/* ---------- media refs (NEVER carry payloads) ---------- */

export type MediaSourceKind = 'storage' | 'idb' | 'remote';

/** Stored on a fabric.CraftaraVideo object as `craftaraVideo`. ~400 bytes. */
export interface VideoClipRef {
  v: 1;
  clipId: string;
  source: MediaSourceKind;
  /** Storage object path. Authoritative — `url` may expire. */
  storagePath?: string;
  url?: string;
  /** Key in the `craftara-video-assets` IndexedDB store. Always set once cached. */
  idbKey?: string;
  mimeType: string;
  naturalWidth: number;
  naturalHeight: number;
  /** Full source duration. */
  durationMs: number;
  /**
   * Source frame rate measured at import (browsers don't expose it). Drives
   * export seek dedupe; absent on clips that couldn't be measured.
   */
  fps?: number;
  /** In/out points inside the source. */
  trimStartMs: number;
  trimEndMs: number;
  /** Playback rate over the trimmed range. */
  rate: number;
  loop: boolean;
  muted: boolean;
  /** 0..1 */
  volume: number;
  /** Poster frame lives in Storage, not inline. Keeps canvasData tiny. */
  posterStoragePath?: string;
  posterIdbKey?: string;
}

export interface AudioFade {
  inMs: number;
  outMs: number;
}

/** The single timeline music track. A ref only — the audio bytes live in Storage/IDB. */
export interface AudioTrack {
  v: 1;
  trackId: string;
  name: string;
  source: MediaSourceKind;
  storagePath?: string;
  url?: string;
  idbKey?: string;
  mimeType: string;
  durationMs: number;
  /** Where on the timeline the track starts. */
  offsetMs: number;
  /** In/out points inside the source file. */
  trimStartMs: number;
  trimEndMs: number;
  /** 0..1 */
  volume: number;
  fade: AudioFade;
  /** Duck music under scene video audio. */
  duckToVideo?: boolean;
}

/* ---------- scenes + project ---------- */

/** Timing metadata for one page when it plays as a scene. */
export interface VideoScene {
  v: 1;
  /** FK to ProjectPage.pageId. Artboard content is never duplicated here. */
  pageId: string;
  durationMs: number;
  /** Transition INTO this scene. Ignored for scenes[0]. */
  transitionIn: SceneTransition;
  /** Letterbox / dip background. */
  bgColor?: string;
  /** Author note. Not rendered. */
  note?: string;
  /** Storage path of the timeline strip poster. Never a data URL. */
  thumbPath?: string;
  /** Scene-local ms at which the strip poster was captured. */
  thumbAtMs?: number;
}

export type VideoContainer = 'mp4' | 'webm';
export type VideoCodecId = 'avc' | 'hevc' | 'vp9' | 'vp8' | 'av1';
export type VideoEncoderBackend = 'webcodecs' | 'mediarecorder';
export type VideoFps = 24 | 25 | 30 | 60;

/** What the user asked for. The encoder planner may downgrade it. */
export interface VideoExportSettings {
  v: 1;
  /** REEL_PLATFORMS id, or 'custom'. */
  platformId: string;
  width: number;
  height: number;
  fps: VideoFps;
  /** Target video bitrate, bps. */
  videoBitrate: number;
  container: VideoContainer;
  videoCodec: VideoCodecId;
  includeAudio: boolean;
  audioBitrate: number;
  audioSampleRate: 44100 | 48000;
  /** 'auto' runs capability detection and may downgrade. */
  backend: VideoEncoderBackend | 'auto';
  /** Keyframe cadence in frames. */
  keyFrameInterval: number;
  quality: 'draft' | 'standard' | 'high';
}

/** Output frame for a v2 timeline. */
export interface VideoCanvas {
  width: number;
  height: number;
  /** Letterbox / empty-frame color. */
  bg: string;
}

export interface VideoBrandColor {
  id: string;
  name: string;
  hex: string;
}

export interface VideoBrandFont {
  id: string;
  name: string;
  family: string;
}

/** Project-local brand. Seeded from the app brand kit; not a second account system. */
export interface VideoBrand {
  name: string;
  colors: VideoBrandColor[];
  fonts: VideoBrandFont[];
  /** Same-origin or storage URL. Bytes are not inlined. */
  logoUrl?: string;
}

export type TimelineTrackKind = 'text' | 'visual' | 'audio' | 'caption';

export interface TimelineTrack {
  id: string;
  kind: TimelineTrackKind;
  name: string;
  /** Visual z-order. Higher draws later. Ignored for audio. */
  index: number;
  muted?: boolean;
  hidden?: boolean;
  locked?: boolean;
}

/** Still image. Same ref rules as VideoClipRef: no payload in the project doc. */
export interface ImageClipRef {
  v: 1;
  source: MediaSourceKind;
  storagePath?: string;
  url?: string;
  idbKey?: string;
  mimeType: string;
  width: number;
  height: number;
  fit: 'cover' | 'contain';
  /** Source rectangle in 0..1. Omit for the whole bitmap. */
  crop?: ImageCrop;
}

/** A region of a bitmap, in fractions of that bitmap. */
export interface ImageCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where a clip sits in the movie frame. Fractions survive a canvas resize.
 * `x` and `y` are the center. `width` is a fraction of the frame width.
 * Omit `height` to keep the picture's aspect.
 */
export interface ClipPlacement {
  x: number;
  y: number;
  width: number;
  height?: number;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
  /** When false, height is free of the content aspect. Default true. */
  lockAspect?: boolean;
}

/** Painted treatment for a title. Omit for ordinary fill. */
export type TextLookId =
  | 'plain'
  | 'creator'
  | 'box'
  | 'pride'
  | 'button'
  | 'bubble'
  | 'retro'
  | 'typewriter'
  | 'circular';

/** Title motion that makes sense on a timeline text clip. */
export type TextMotionId =
  | 'none'
  | 'fade-in'
  | 'rise'
  | 'pop-in'
  | 'fade-out'
  | 'sink'
  | 'pop-out';

export interface TextClipStyle {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fill: string;
  /** Style card from the text menu. */
  look?: TextLookId;
  align: 'left' | 'center' | 'right';
  /** Anchor in the frame, 0..1. */
  x: number;
  y: number;
  inPreset?: TextMotionId;
  outPreset?: TextMotionId;
  /** Full-frame card behind the words. Templates use this instead of a stock bitmap. */
  background?: string;
  gradientTo?: string;
  fullFrame?: boolean;
  /** Colored bar tight around the words. `background` is the bar color. */
  bar?: boolean;
}

/** One placed music or voice clip. Timing on the timeline lives on TimelineClip. */
export interface AudioClipRef {
  v: 1;
  source: MediaSourceKind;
  storagePath?: string;
  url?: string;
  idbKey?: string;
  mimeType: string;
  durationMs: number;
  trimStartMs: number;
  trimEndMs: number;
  /** 0..1 */
  volume: number;
  fade: AudioFade;
}

export interface CaptionWord {
  text: string;
  /** Clip-local ms. */
  startMs: number;
  endMs: number;
}

export interface CaptionClip {
  text: string;
  source: 'manual' | 'auto';
  words?: CaptionWord[];
  fill?: string;
  fontFamily?: string;
  fontSize?: number;
}

export type TimelineClipKind = 'video' | 'image' | 'text' | 'audio' | 'caption' | 'scene';

/**
 * One item on a track. `startMs` is free — clips do not have to butt together.
 * Overlap on one visual track is the transition window.
 */
export interface TimelineClip {
  id: string;
  trackId: string;
  startMs: number;
  durationMs: number;
  name: string;
  /** Template drops share a group until the user ungroups them. */
  groupId?: string;
  kind: TimelineClipKind;
  transitionIn?: SceneTransition;
  video?: VideoClipRef;
  image?: ImageClipRef;
  text?: TextClipStyle;
  audio?: AudioClipRef;
  caption?: CaptionClip;
  scene?: { pageId: string };
  /** Frame placement for a photo, video, or title. Omit to use the legacy fit. */
  placement?: ClipPlacement;
  /** Paint order inside a track. Higher draws later. */
  z?: number;
}

/** Persisted inline on the project doc as `Project.video`. */
export interface VideoProject {
  /** 1 = scene lane. 2 = multi-track Movie Studio timeline. */
  v: 1 | 2;
  /** False until the user turns on Motion mode. Keeps non-video projects clean. */
  enabled: boolean;
  /**
   * v1 scene lane. v2 keeps this so a migrated movie can still find video
   * layers that live inside Fabric pages. New clip movies store [].
   */
  scenes: VideoScene[];
  /** v1's single music slot. v2 music is an audio clip. */
  audio?: AudioTrack | null;
  canvas?: VideoCanvas;
  tracks?: TimelineTrack[];
  clips?: TimelineClip[];
  brand?: VideoBrand;
  exportSettings: VideoExportSettings;
  /** Denormalized for UI; recomputed on every mutation. */
  totalDurationMs: number;
  /** epoch ms — feeds the autosave change hash. */
  updatedAt: number;
}

/* ---------- runtime-only (videoStore, never persisted) ---------- */

export type PlaybackState = 'stopped' | 'playing' | 'paused' | 'scrubbing';

/** Resolved scene window on the global timeline, transition overlap included. */
export interface SceneCue {
  pageId: string;
  index: number;
  startMs: number;
  endMs: number;
  /** Overlap with the previous scene (its transitionIn duration). */
  overlapInMs: number;
  /** Overlap with the next scene. */
  overlapOutMs: number;
}

/** Timeline UI state. Playhead position is not a document property. */
export interface TimelineState {
  playback: PlaybackState;
  currentTimeMs: number;
  /** Ruler scale. */
  pxPerSec: number;
  /** Selected scene = its pageId. */
  selectedSceneId: string | null;
  /** Selected layer = its __uid. */
  selectedLayerUid: string | null;
  loop: boolean;
  muted: boolean;
  /** Derived from scenes; recomputed by buildCues(). */
  cues: SceneCue[];
}

/* ---------- limits ---------- */

export const VIDEO_SCHEMA_VERSION = 1 as const;
export const VIDEO_TIMELINE_VERSION = 2 as const;
export const VIDEO_MAX_SCENES = 60;
export const VIDEO_MAX_CLIPS = 200;
export const VIDEO_MIN_CLIP_MS = 100;
export const VIDEO_MAX_TOTAL_MS = 5 * 60_000;
export const VIDEO_MIN_SCENE_MS = 300;
export const VIDEO_MAX_SCENE_MS = 60_000;
export const VIDEO_MAX_TRANSITION_MS = 2_000;
/** Firestore hard limit is 1 MiB; warn well before. */
export const PROJECT_DOC_SOFT_LIMIT_BYTES = 800_000;
